import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { GOOGLE_FORM_URL } from "@/constants/links";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Destinations", href: "#destinations" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Voices", href: "#testimonials" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-black/5"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <span className="font-display text-2xl md:text-[28px] font-bold tracking-tighter text-[#0a192f]">
            Edufly<span className="text-[#00A3FF]">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              className="font-label text-[11px] text-[#0a192f]/80 hover:text-[#0a192f] transition-colors relative"
              data-testid={`nav-link-${i}`}
            >
              <span className="font-mono-tech mr-1 opacity-40">0{i + 1}</span>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill btn-navy hidden md:inline-flex text-[12px] px-5 py-2.5"
            data-testid="nav-cta-consultation"
          >
            Book Consultation
          </a>
          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#0a192f]/15"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l, i) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="font-display text-2xl text-[#0a192f]"
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${i}`}
                >
                  <span className="font-mono-tech text-sm mr-2 opacity-40">
                    0{i + 1}
                  </span>
                  {l.label}
                </a>
              ))}
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="btn-pill btn-navy mt-2"
                data-testid="nav-mobile-cta"
              >
                Book Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}